/**
 * Prix produit — source unique d'affichage côté client.
 *
 * L'API expose deux formes qui ne portent PAS le même montant :
 *
 * 1. Produit (`GET /products`, `GET /products/{id}`) :
 *    `sale_price = { simple, normal }`, où `simple` = échantillon et `normal` =
 *    gros. Les deux valeurs sortent déjà de `sale_price_with_bank_fees` côté
 *    backend (frais bancaires inclus) et correspondent à la variante la moins
 *    chère du type.
 *
 * 2. Variante (`?with_variants=true` → `variants[].normal` / `variants[].sample`) :
 *    `sale_price` est le prix BRUT, hors frais bancaires ; le prix client se lit
 *    dans `sale_price_with_bank_fees`.
 *
 * Afficher `variant.sale_price` tel quel sous-facture le client des frais
 * bancaires, et la fiche produit annonce alors moins cher que la carte de la
 * liste. Passer par ces helpers, jamais par les champs bruts.
 *
 * Deux pièges à ne pas réintroduire :
 * - `Object.values(sale_price).find(v => typeof v === 'number')` renvoie la
 *   PREMIÈRE clé, c'est-à-dire `simple` : un produit en gros affichait le prix
 *   de son échantillon. Toujours choisir la clé par type.
 * - `crossed_out_price` reste brut : comparer un prix avec frais à un prix barré
 *   sans frais fausse la remise. La remise se calcule sur les montants bruts,
 *   le pourcentage est identique (même facteur des deux côtés).
 */

/** Clé de `sale_price` correspondant à chaque type de produit. */
const SALE_PRICE_KEY = { normal: 'normal', sample: 'simple' };

/** Convertit un montant API (nombre ou chaîne) en nombre exploitable. */
export const parsePrice = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

/**
 * Prix client d'une variante (`variants[].normal` ou `variants[].sample`),
 * frais bancaires inclus.
 */
export const variantPrice = (side) =>
  parsePrice(side?.sale_price_with_bank_fees ?? side?.sale_price);

/**
 * Prix client au niveau produit pour un type donné, frais bancaires inclus.
 * @param {object} product
 * @param {"normal"|"sample"} type
 */
export const productPrice = (product, type = 'normal') => {
  const salePrice = product?.sale_price;
  if (typeof salePrice === 'number' || typeof salePrice === 'string') {
    return parsePrice(salePrice);
  }
  return parsePrice(salePrice?.[SALE_PRICE_KEY[type] ?? type]);
};

/**
 * Prix produit sur un flux mixte (accueil, recherche) : le gros d'abord, à
 * défaut l'échantillon pour un produit vendu uniquement en échantillon.
 */
export const anyProductPrice = (product) =>
  productPrice(product, 'normal') || productPrice(product, 'sample');

/**
 * Aplatit les combinaisons d'un produit chargé avec `with_variants=true`.
 * `sale_price` est réécrit avec les frais bancaires, `sale_price_raw` conserve
 * le montant brut (base de calcul des remises).
 *
 * @param {object} product
 * @param {"normal"|"sample"} type
 */
export const flattenVariants = (product, type = 'normal') => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const key = type === 'sample' ? 'sample' : 'normal';

  return variants
    .filter((v) => v?.[key])
    .map((v) => ({
      ...v[key],
      sale_price: variantPrice(v[key]),
      sale_price_raw: parsePrice(v[key].sale_price),
      variant: v,
    }));
};

/**
 * Fourchette de prix d'une liste de variantes déjà aplaties.
 * @returns {{ min:number, max:number, hasRange:boolean }}
 */
export const priceRange = (variants = []) => {
  const prices = variants.map((v) => variantPrice(v)).filter((n) => n > 0);
  if (prices.length === 0) return { min: 0, max: 0, hasRange: false };

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { min, max, hasRange: max > min };
};

/**
 * Remise à partir de deux montants (vente vs barré / marché).
 * @returns {number|null} Pourcentage entier, ou null si pas de remise.
 */
export const discountFromPrices = (sale, crossed) => {
  const salePrice = parsePrice(sale);
  const crossedPrice = parsePrice(crossed);
  if (!salePrice || !crossedPrice || crossedPrice <= salePrice) return null;
  return Math.round(100 - (salePrice * 100) / crossedPrice);
};

/**
 * Remise affichée sur un échantillon / variante.
 * Calculée sur les montants bruts : `crossed_out_price` n'est jamais majoré des
 * frais bancaires, et le pourcentage est le même sur les deux bases.
 */
export const discountPercent = (side) =>
  discountFromPrices(side?.sale_price_raw ?? side?.sale_price, side?.crossed_out_price);

/**
 * Remise au niveau produit (listes Stock Local / accueil).
 * Ordre : `sample`/`normal` → variantes → `local_market_price` (souvent
 * présent sur `GET /products` alors que `crossed_out_price` ne l’est pas).
 *
 * @param {object} product
 * @param {"normal"|"sample"} type
 */
export const productDiscountPercent = (product, type = 'sample') => {
  if (!product) return null;

  const sideKey = type === 'sample' ? 'sample' : 'normal';
  let discount = discountPercent(product[sideKey]);
  if (discount != null) return discount;

  if (Array.isArray(product.variants)) {
    for (const variant of product.variants) {
      discount = discountPercent(variant?.[sideKey]);
      if (discount != null) return discount;
    }
  }

  return discountFromPrices(productPrice(product, type), product.local_market_price);
};

/** Montant formaté à la française, sans devise (« 12 000 »). */
export const formatAmount = (value) =>
  parsePrice(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
